import { useMutation } from '@apollo/client';
import { useState, useCallback } from 'react';
import { removeInspectionPhotoMutation } from 'graphql-queries';

export interface RemoveInspectionPhotoInput {
  channelId: string;
  instanceId: string;
  itemId: string;
  photoId: string;
  detachedAt: Date;
}

type RemoveInspectionPhotoMutationResult = {
  removeInspectionPhoto: {
    success: boolean;
    newVersion: number;
  } | null;
};

type RemoveInspectionPhotoMutationVariables = {
  input: {
    channelId: string;
    instanceId: string;
    itemId: string;
    photoId: string;
    detachedAt: string;
  };
};

export interface UseRemoveInspectionPhotoResult {
  removePhoto: (
    input: RemoveInspectionPhotoInput
  ) => Promise<{ success: boolean; newVersion: number }>;
  loading: boolean;
  error: Error | undefined;
}

export function useRemoveInspectionPhoto(): UseRemoveInspectionPhotoResult {
  const [mutate] = useMutation<
    RemoveInspectionPhotoMutationResult,
    RemoveInspectionPhotoMutationVariables
  >(removeInspectionPhotoMutation);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  const removePhoto = useCallback(
    async (input: RemoveInspectionPhotoInput) => {
      setLoading(true);
      setError(undefined);

      try {
        const result = await mutate({
          variables: {
            input: {
              channelId: input.channelId,
              instanceId: input.instanceId,
              itemId: input.itemId,
              photoId: input.photoId,
              detachedAt: input.detachedAt.toISOString(),
            },
          },
        });

        if (!result.data?.removeInspectionPhoto?.success) {
          throw new Error('Failed to remove inspection photo');
        }

        return {
          success: result.data.removeInspectionPhoto.success,
          newVersion: result.data.removeInspectionPhoto.newVersion,
        };
      } catch (err) {
        const wrappedError =
          err instanceof Error ? err : new Error('Unknown error occurred');
        setError(wrappedError);
        throw wrappedError;
      } finally {
        setLoading(false);
      }
    },
    [mutate]
  );

  return {
    removePhoto,
    loading,
    error,
  };
}
